import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { ordersService } from './orders.services';

import type { ChangeStatusResponse, OrderListFilters, OrderListResponse, OrderMetrics } from './orders.types';

/**
 * Query Key Factory for the Orders domain.
 */
export const ordersQueryKeys = {
  all: ['orders'] as const,
  lists: () => [...ordersQueryKeys.all, 'list'] as const,
  list: (filters?: OrderListFilters) => [...ordersQueryKeys.lists(), filters] as const,
  metrics: () => [...ordersQueryKeys.all, 'metrics'] as const,
};

/**
 * Returns the list of orders from the real backend API.
 * Endpoint: GET /admin/orders
 *
 * @param filters - Optional filters for the list.
 */
export const useGetOrders = (filters?: OrderListFilters) => {
  return useQuery({
    queryKey: ordersQueryKeys.list(filters),
    queryFn: (): Promise<OrderListResponse> => ordersService.list(filters),
  });
};

/**
 * Returns order metrics aggregated by status.
 * Endpoint: GET /admin/orders/metrics
 */
export const useGetOrdersMetrics = () => {
  return useQuery({
    queryKey: ordersQueryKeys.metrics(),
    queryFn: (): Promise<OrderMetrics> => ordersService.metrics(),
  });
};

export const useGetEvidenceReadUrl = (gcs_path: string | null | undefined) => {
  return useQuery({
    queryKey: [...ordersQueryKeys.all, 'evidence', gcs_path] as const,
    queryFn: () => ordersService.getEvidenceReadUrl(gcs_path!),
    enabled: !!gcs_path,
    staleTime: 1000 * 60 * 10, // 10 minutes (signed URLs usually expire)
  });
};

export const useChangeStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      order_refs: string[];
      external_reference?: string; // First selected order's guide
      action: 'delete' | string;
      motivo: string;
      evidence_file?: File;
    }): Promise<ChangeStatusResponse> => {
      let evidence_gcs_path: string | undefined;

      if (payload.evidence_file && payload.external_reference) {
        const file = payload.evidence_file;
        const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';

        const signed = await ordersService.getEvidenceSignedUrl(payload.external_reference, ext);
        await ordersService.uploadToGcs(signed.upload_url, file);
        evidence_gcs_path = signed.gcs_path;
      }

      return ordersService.changeStatus({
        order_refs: payload.order_refs,
        action: payload.action,
        motivo: payload.motivo,
        evidence_gcs_path,
      });
    },
    onSuccess: (data) => {
      if (data.updated.length > 0) {
        toast.success(`${data.updated.length} orden(es) actualizada(s) correctamente.`);
        queryClient.invalidateQueries({ queryKey: ordersQueryKeys.all });
      }
    },
    onError: () => {
      toast.error('Ocurrió un error al intentar cambiar el estado de las órdenes.');
    },
  });
};
